import { readonly, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useAuthService } from '@/authuser/services'
import axios from '@/base/api/axios'
import { useHasGroupSanction } from '@/group/helpers.js'
import { useCurrentGroupService, useSanctionService } from '@/group/services'
import { canViewIssueReport } from '@/issues/helpers.js'
import { useActiveIssueService } from '@/issues/services.js'
import { defineService, isValidationError } from '@/utils/datastore/helpers'

export const useRouteErrorService = defineService(() => {
  const hasError = ref(false)
  const message = ref(null)

  const router = useRouter()
  router.beforeEach(() => {
    hasError.value = false
    message.value = null
  })

  function setRouteError (messageValue = null) {
    hasError.value = true
    message.value = messageValue
  }

  return {
    setRouteError,
    hasError: readonly(hasError),
    message: readonly(message),
  }
})

export const useCheckResponseAuthStatus = defineService(() => {
  const { refresh } = useAuthService()
  axios.interceptors.response.use(response => response, async error => {
    if (isValidationError(error)) {
      if (error.response.status === 403) {
        const url = error.request?.responseURL || error.response.request?.responseURL
        if (url && !url.endsWith('/api/auth/user/')) {
          await refresh()
        }
      }
    }
    throw error
  })
})

export const useRoutingLogic = defineService(() => {
  const router = useRouter()
  const route = useRoute()

  const {
    user,
    isLoggedIn,
    waitForUserToLoad,
  } = useAuthService()

  const {
    groupId,
    features,
    waitForGroupToLoad,
  } = useCurrentGroupService()

  const { waitForSanctionsToLoad } = useSanctionService()
  const hasGroupSanction = useHasGroupSanction()

  // If we find we are logged out, but on a logged in page, go to login page
  watch(isLoggedIn, value => {
    if (!value && route.matched.some(m => m.meta.requireLoggedIn)) {
      router.push({ name: 'login', query: { to: route.fullPath } })
    }
  })

  const { preloadIssue } = useActiveIssueService()

  router.beforeEach(async (to, from, nextFn) => {
    let next

    if (to.meta.noRedirect) {
      nextFn()
      return
    }

    await waitForUserToLoad()

    const requiredGroupFeatures = to.matched.map(m => m.meta.requireFeature).filter(Boolean)

    if (isLoggedIn.value) {
      await waitForSanctionsToLoad() // they got loaded in bootstrap call probably, but just incase not...

      // Check if we are sanctioned for the group we are going to!
      if (to.params.groupId && hasGroupSanction(parseInt(to.params.groupId))) {
        return nextFn({ name: 'groupPreview', params: { groupPreviewId: to.params.groupId } })
      }
    }

    // redirect homescreen correctly
    // the home page is a meta page, and doesn't have any content so we have to choose a suitable page to send them to
    if (to.path === '/') {
      if (groupId.value) {
        next = { name: 'group', params: { groupId: groupId.value } }
      }
      else if (isLoggedIn.value) {
        next = { name: 'groupsGallery' }
      }
      else {
        next = { name: 'landing' }
      }
    }

    // check meta.requireLoggedIn
    else if (to.matched.some(m => m.meta.requireLoggedIn) && !isLoggedIn.value) {
      next = { name: 'login', query: { to: to.fullPath } }
    }

    // check meta.requireInstanceAdmin
    else if (to.matched.some(m => m.meta.requireInstanceAdmin) && !user.value.isInstanceAdmin) {
      console.log('user is not instance admin!', user.value, { from })
      // return false // nope!
      nextFn(false)
      return
    }

    // check meta.requireLoggedOut
    else if (to.matched.some(m => m.meta.requireLoggedOut) && isLoggedIn.value) {
      next = { path: '/' }
    }

    // check if we have the required features
    else if (requiredGroupFeatures.length > 0) {
      // need our group to be ready to  check the features
      await waitForGroupToLoad()

      if (requiredGroupFeatures.some(feature => !features.value.includes(feature))) {
        if (groupId.value) {
          next = { name: 'group', params: { groupId: groupId.value } }
        }
        else {
          next = { path: '/' }
        }
      }
    }

    // We have some special handling for issueDetail route so we can
    // load the issue during this navigation guard and do some logic on
    // where to go next, as it depends on the issue data
    if (to.name === 'issueDetail') {
      const issue = await preloadIssue(to.params.issueId)
      if (!issue) {
        next = { name: 'group', params: { groupId: groupId.value } }
      }
      else if (issue.type === 'report') {
        // Reports always go to the issue report page
        if (!canViewIssueReport(issue)) {
          // Nope, can't view the issue, go back to the list!
          next = { name: 'issueList', params: { groupId: groupId.value } }
        }
        else {
          next = { name: 'issueReport', params: { groupId: groupId.value, issueId: issue.id } }
        }
      }
      else {
        // Ongoing issues go to chat tab, otherwise to the voting tab
        next = {
          name: issue.status === 'ongoing' ? 'issueChat' : 'issueVote',
          params: { groupId: groupId.value, issueId: issue.id },
          query: to.query,
        }
      }
    }

    if (next) {
      nextFn(next)
      return
    }

    nextFn()
  })
})
